import {
  useMediaQuery,
  WhmAutocomplete,
  WhmButton,
  WhmCheckbox,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmDivider,
  WhmFormControlLabel,
  WhmGrid,
  WhmIconButton,
  WhmLoadingButton,
  WhmStack,
  WhmTextField,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../../contexts/I18nContext";
import { useContext, useId, useRef, useState } from "react";
import useUserInfo from "../../../../hooks/useUserInfo";

import { Form, Formik } from "formik";
import * as yup from "yup";
import { SnackbarContext } from "../../../shared/Snackbar";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import { createKeyParty, updateKeyParty } from "../../../../data/api";
import { useQueryClient } from "@tanstack/react-query";
import { SID } from "@securitize/domain";
import {
  CreateKeyPartyPayloadDto,
  UboOwning,
} from "../../../../data/dtos/create-key-party.dto";
import { ResponseError } from "../../../../data/types/responseError";
import { ReactComponent as CloseIcon } from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";
import { showsAsEnhanced } from "./utils";
import FormikWhmTextField from "../../../shared/FormikWhmTextField";
import { REQUIRED_GENERIC_HIDDEN_MESSAGE } from "../../../../utils/yupUtils";
import { ReactComponent as CheckMark } from "@securitize/assets-sec-ui/dist/icons/svg/check.svg";
import { KeyParty } from "../../../../data/dtos/get-key-party.dto";
import { entityTypesTranslations } from "../../../../contexts/I18nContext/translationKeys";
import { allowedEntityTypes } from "../../information/entity/entityTypeList";
import { allowedEntityNameCharsRegex } from "../../../../utils/validations";

export default function AddEntityDialog({
  open,
  keyParty,
  onClose,
  openCompleteDetails,
  readOnly = false,
}: {
  open: boolean;
  keyParty?: KeyParty;
  onClose: () => void;
  openCompleteDetails: (keyParty: KeyParty) => void;
  readOnly?: boolean;
}) {
  const user = useUserInfo();
  const enhanced = showsAsEnhanced(user);
  const snackbar = useContext(SnackbarContext);
  const addEntityFormId = useId();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const { getTranslation } = useContext(I18nContext);
  const [displayTopDivider, setDisplayTopDivider] = useState(false);
  const [displayBottomDivider, setDisplayBottomDivider] = useState(false);

  const dialogContentRef = useRef<HTMLDivElement>(null);

  const entityOptions = Object.values(allowedEntityTypes).map((e) => ({
    value: e,
    label: getTranslation(entityTypesTranslations[e]),
  }));

  return (
    <WhmDialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? "xl" : "md"}
      scroll="paper"
      TransitionProps={{
        onEnter: () => {
          if (dialogContentRef.current) {
            if (
              dialogContentRef.current.scrollHeight >
              dialogContentRef.current.clientHeight
            ) {
              setDisplayBottomDivider(true);
            }
          }
        },
      }}
    >
      <Formik
        initialValues={{
          signerType: "entity" as SID.InvestorType,
          legalName: "",
          entityType: "",
          uboOwning: "none" as UboOwning,
          ...keyParty,
        }}
        onSubmit={async (values, helpers) => {
          helpers.setSubmitting(true);
          try {
            if (keyParty?.id) {
              const response = await updateKeyParty(keyParty.id, values);

              await queryClient.invalidateQueries({
                predicate: (query) =>
                  ["key-parties", "onboarding", "pending-actions"].includes(
                    query.queryKey[0] as string,
                  ),
              });
              snackbar.showSnackbar({
                severity: "info",
                message: (
                  <WhmStack direction="row" alignItems="center" gap="16px">
                    <CheckMark fill="white" />
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_UPDATE_ENTITY_SUCCESS_MESSAGE
                      }
                      replacements={{
                        name: response.displayName,
                      }}
                    />
                  </WhmStack>
                ),
              });
            } else {
              const response = await createKeyParty(
                values as CreateKeyPartyPayloadDto,
              );

              await queryClient.invalidateQueries({
                predicate: (query) =>
                  ["key-parties", "onboarding", "pending-actions"].includes(
                    query.queryKey[0] as string,
                  ),
              });
              snackbar.showSnackbar({
                severity: "info",
                message: (
                  <WhmStack direction="row" alignItems="center" gap="16px">
                    <CheckMark fill="white" />

                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_ADD_ENTITY_SUCCESS_MESSAGE
                      }
                      replacements={{
                        name: response.displayName,
                      }}
                    />

                    <WhmButton
                      variant="outlined"
                      size="small"
                      sx={{
                        color: whmTheme.palette.common.white,
                        borderColor: whmTheme.palette.common.white,
                        width: { xs: "100%", xl: "auto" },
                        minWidth: "fit-content !important",
                        "&:hover": {
                          borderColor: whmTheme.palette.common.white,
                        },
                      }}
                      onClick={() => openCompleteDetails(response)}
                    >
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ADD_ENTITY_SUCCESS_COMPLETE_DETAILS_BUTTON
                        }
                      />
                    </WhmButton>
                  </WhmStack>
                ),
              });
            }

            onClose();
          } catch (e) {
            console.error(e);
            snackbar.showSnackbar({
              message: <ErrorSnackbar error={e as ResponseError} />,
              severity: "error",
            });
          } finally {
            helpers.setSubmitting(false);
          }
        }}
        validationSchema={yup.object({
          legalName: yup
            .string()
            .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
            .matches(
              allowedEntityNameCharsRegex,
              getTranslation(
                translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_NAME_INVALID,
              ),
            ),
          entityType: yup.string().required(REQUIRED_GENERIC_HIDDEN_MESSAGE),
        })}
      >
        {(formik) => (
          <>
            <WhmDialogTitle variant="h4">
              {keyParty?.id ? (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_UPDATE_ENTITY_TITLE
                  }
                />
              ) : (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_KEY_PARTIES_ADD_ENTITY_TITLE
                  }
                />
              )}
            </WhmDialogTitle>
            <WhmIconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                right: "8px",
                top: "8px",
              }}
            >
              <CloseIcon />
            </WhmIconButton>
            {displayTopDivider && <WhmDivider />}
            <WhmDialogContent
              ref={dialogContentRef}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, offsetHeight } =
                  e.currentTarget;

                setDisplayTopDivider(scrollTop !== 0);
                setDisplayBottomDivider(
                  scrollTop !== scrollHeight - offsetHeight,
                );
              }}
            >
              <Form id={addEntityFormId}>
                <WhmStack gap="16px">
                  <WhmTypography>
                    <Translate
                      translationKey={
                        translationKeys.PROFILE_KEY_PARTIES_ADD_ENTITY_SUBTITLE
                      }
                    />
                  </WhmTypography>
                  <WhmStack>
                    <WhmTypography variant="subtitle1">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ENTITY_INFORMATION
                        }
                      />
                    </WhmTypography>
                    <WhmTypography variant="body2" color="text.secondary">
                      <TranslateHtml
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ENTITY_INFORMATION_SUBTITLE
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                  {!formik.isValid &&
                    formik.submitCount > 0 &&
                    (formik.values.legalName === "" ||
                      formik.values.entityType === "") && (
                      <WhmTypography variant="body2" color="error">
                        <Translate
                          translationKey={
                            translationKeys.PROFILE_KEY_PARTIES_VALIDATION_ERROR
                          }
                        />
                      </WhmTypography>
                    )}
                  <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
                    <WhmGrid item xs={6}>
                      <FormikWhmTextField
                        label={
                          <Translate
                            translationKey={
                              translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_NAME
                            }
                          />
                        }
                        fullWidth
                        name="legalName"
                        error={
                          formik.touched.legalName &&
                          (formik.errors.legalName ===
                          REQUIRED_GENERIC_HIDDEN_MESSAGE
                            ? formik.submitCount > 0
                            : Boolean(formik.errors.legalName))
                        }
                        disabled={readOnly || formik.isSubmitting}
                      />
                    </WhmGrid>
                    <WhmGrid item xs={6}>
                      <WhmAutocomplete
                        fullWidth
                        sx={{ maxWidth: "400px" }}
                        value={
                          entityOptions.find(
                            (o) => o.value === formik.values.entityType,
                          ) ?? null
                        }
                        onChange={(_, v) =>
                          void formik.setFieldValue(
                            "entityType",
                            v?.value ?? "",
                          )
                        }
                        options={entityOptions}
                        renderInput={(props) => (
                          <WhmTextField
                            {...props}
                            InputProps={{
                              // eslint-disable-next-line react/prop-types -- React types issue
                              ...props.InputProps,
                              sx: { backgroundColor: "white" },
                            }}
                            data-test-id="entity-type-input"
                            name="entityType"
                            error={
                              formik.touched.entityType &&
                              Boolean(formik.errors.entityType)
                            }
                            onBlur={formik.handleBlur}
                            helperText={
                              formik.touched.entityType &&
                              formik.errors.entityType !==
                                REQUIRED_GENERIC_HIDDEN_MESSAGE
                                ? formik.errors.entityType
                                : undefined
                            }
                            placeholder={getTranslation(
                              translationKeys.MISSING_ENTITY_TYPE_PLACEHOLDER,
                            )}
                            label={
                              <Translate
                                translationKey={
                                  translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_TYPE
                                }
                              />
                            }
                          />
                        )}
                        disabled={readOnly || formik.isSubmitting}
                      />
                    </WhmGrid>
                  </WhmGrid>
                  <WhmDivider />
                  <WhmStack>
                    <WhmTypography variant="subtitle1">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_RELATIONSHIP_TITLE
                        }
                      />
                    </WhmTypography>
                    <WhmTypography variant="body2" color="text.secondary">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_RELATIONSHIP_DESCRIPTION
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                  <WhmGrid container columns={{ xs: 6, md: 12 }} spacing="16px">
                    <WhmGrid item xs={6}>
                      {enhanced ? (
                        <WhmFormControlLabel
                          disabled={readOnly || formik.isSubmitting}
                          sx={{
                            flex: 1,
                            marginRight: 0,
                            alignItems: "flex-start",
                          }}
                          control={
                            <WhmCheckbox
                              checked={formik.values.uboOwning === "10+"}
                              onChange={() => {
                                if (formik.values.uboOwning === "10+") {
                                  formik
                                    .setFieldValue("uboOwning", "none")
                                    .catch((e) => console.error(e));
                                } else {
                                  formik
                                    .setFieldValue("uboOwning", "10+")
                                    .catch((e) => console.error(e));
                                }
                              }}
                            />
                          }
                          label={
                            <WhmStack>
                              <WhmTypography>
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_10_PERCENT_UBO_LABEL
                                  }
                                />
                              </WhmTypography>
                              <WhmTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_10_PERCENT_UBO_DESCRIPTION
                                  }
                                />
                              </WhmTypography>
                            </WhmStack>
                          }
                        />
                      ) : (
                        <WhmFormControlLabel
                          disabled={readOnly || formik.isSubmitting}
                          sx={{
                            flex: 1,
                            marginRight: 0,
                            alignItems: "flex-start",
                          }}
                          control={
                            <WhmCheckbox
                              checked={formik.values.uboOwning === "25+"}
                              onChange={() => {
                                if (formik.values.uboOwning === "25+") {
                                  formik
                                    .setFieldValue("uboOwning", "none")
                                    .catch((e) => console.error(e));
                                } else {
                                  formik
                                    .setFieldValue("uboOwning", "25+")
                                    .catch((e) => console.error(e));
                                }
                              }}
                            />
                          }
                          label={
                            <WhmStack>
                              <WhmTypography>
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_25_PERCENT_UBO_LABEL
                                  }
                                />
                              </WhmTypography>
                              <WhmTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                <Translate
                                  translationKey={
                                    translationKeys.PROFILE_KEY_PARTIES_25_PERCENT_UBO_DESCRIPTION
                                  }
                                />
                              </WhmTypography>
                            </WhmStack>
                          }
                        />
                      )}
                    </WhmGrid>
                  </WhmGrid>
                </WhmStack>
              </Form>
            </WhmDialogContent>
            {displayBottomDivider && <WhmDivider />}
            <WhmDialogActions>
              <WhmButton variant="outlined" color="secondary" onClick={onClose}>
                <Translate
                  translationKey={translationKeys.PROFILE_KEY_PARTIES_CANCEL}
                />
              </WhmButton>
              {!readOnly && (
                <WhmLoadingButton
                  variant="contained"
                  type="submit"
                  data-test-id="add-entity-submit-button"
                  form={addEntityFormId}
                  loading={formik.isSubmitting}
                  sx={{
                    svg: {
                      display: "block !important",
                    },
                    "&.MuiLoadingButton-loading": {
                      color: "black",
                      opacity: "0.3",
                      span: {
                        color: "black",
                      },
                    },
                  }}
                >
                  {keyParty?.id &&
                    (formik.isSubmitting ? (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_UPDATE_ENTITY_SUBMIT_IN_PROGRESS
                        }
                      />
                    ) : (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_UPDATE_ENTITY_SUBMIT
                        }
                      />
                    ))}
                  {!keyParty?.id &&
                    (formik.isSubmitting ? (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ADD_ENTITY_SUBMIT_IN_PROGRESS
                        }
                      />
                    ) : (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_ADD_ENTITY_SUBMIT
                        }
                      />
                    ))}
                </WhmLoadingButton>
              )}
            </WhmDialogActions>
          </>
        )}
      </Formik>
    </WhmDialog>
  );
}
